import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link2, Plus, Copy, Trash2, RefreshCw, ExternalLink, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { getActiveStudioId, isSuperAdmin } from "@/lib/api";
import BottomNav, { Sidebar } from "@/components/Navigation";
import { format } from "date-fns";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";
import { useConfirmDialog } from "@/components/ConfirmDialog";

const JoinLinksManagement = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState(getActiveStudioId() || "");
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");

  const canManage = isSuperAdmin(user) || user?.role === "admin";

  const baseUrl = window.location.origin;

  useEffect(() => {
    api.get("/studios")
      .then((r) => {
        const list = r.data?.studios || [];
        setStudios(list);
        if (!studioId && list.length > 0) setStudioId(list[0].studio_id);
      })
      .catch(() => toast.error("Failed to load studios"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = async () => {
    if (!studioId) return;
    setLoading(true);
    try {
      const r = await api.get(`/join-links?studio_id=${studioId}`);
      setLinks(r.data?.join_links || []);
    } catch (e) {
      toast.error("Failed to load join links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studioId) reload();
  }, [studioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!studioId) return;
    setCreating(true);
    try {
      await api.post("/join-links", { studio_id: studioId, label: label.trim() || undefined });
      setLabel("");
      toast.success("Join link created");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token) => {
    const url = `${baseUrl}/join/${token}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Link copied to clipboard"),
      () => toast.error("Couldn't copy — please copy manually")
    );
  };

  const revoke = async (token) => {
    const ok = await confirm({
      title: "Revoke this join link?",
      message: "Anyone with this URL will see a 'link unavailable' page. This action cannot be undone.",
      confirmText: "Revoke",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.post(`/join-links/${token}/revoke`);
      toast.success("Link revoked");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke");
    }
  };

  const remove = async (token) => {
    const ok = await confirm({
      title: "Delete this join link?",
      message: "This permanently deletes the link entry. Students already registered through it stay intact.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/join-links/${token}`);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const activeLinks = useMemo(() => links.filter((l) => l.is_active), [links]);
  const revokedLinks = useMemo(() => links.filter((l) => !l.is_active), [links]);

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0">
      <div className="flex">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 p-4 md:p-8 md:ml-64">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <header className="mb-6 flex items-center gap-3">
              <Link2 className="w-7 h-7 text-violet-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Join Links</h1>
                <p className="text-sm text-slate-500">Shareable links for parents/students to register themselves.</p>
              </div>
            </header>

            {/* Studio selector */}
            {studios.length > 1 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <select
                  data-testid="join-link-studio-select"
                  value={studioId}
                  onChange={(e) => setStudioId(e.target.value)}
                  className="flex-1 h-10 rounded-lg border border-slate-200 px-3 bg-white"
                >
                  {studios.map((s) => (
                    <option key={s.studio_id} value={s.studio_id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Create new */}
            {canManage && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
                <h2 className="text-base font-semibold mb-3">Create a new link</h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Label (internal, optional)</Label>
                    <Input
                      data-testid="join-link-label-input"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g. Instagram Bio Link, Reception QR"
                      className="h-11 rounded-xl"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      data-testid="join-link-create-btn"
                      onClick={handleCreate}
                      disabled={creating || !studioId}
                      className="h-11 rounded-xl text-white"
                      style={{ backgroundColor: "var(--brand-primary)" }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Generate Link
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Active links */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Active ({activeLinks.length})</h2>
                <Button size="sm" variant="ghost" onClick={reload} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
              {loading && <p className="text-sm text-slate-500">Loading…</p>}
              {!loading && activeLinks.length === 0 && (
                <p className="text-sm text-slate-500 bg-white rounded-xl border border-dashed border-slate-200 p-6 text-center">
                  No active join links yet. Create one above and share it on WhatsApp, Instagram bio, posters, etc.
                </p>
              )}
              {activeLinks.map((l) => {
                const url = `${baseUrl}/join/${l.token}`;
                return (
                  <div
                    key={l.token}
                    data-testid={`join-link-row-${l.token}`}
                    className="bg-white rounded-2xl border border-slate-200 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-900">{l.label || "Untitled link"}</span>
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded-full">Active</span>
                          <span className="text-[10px] text-slate-400">
                            {l.submission_count || 0} signup{l.submission_count === 1 ? "" : "s"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 truncate">{url}</p>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Created {fmt(l.created_at, "dd MMM yyyy", default_timezone)}
                          {l.last_used_at ? ` · Last used ${fmt(l.last_used_at, "dd MMM yyyy", default_timezone)}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`join-link-copy-${l.token}`}
                          onClick={() => copyLink(l.token)}
                          className="rounded-lg"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(url, "_blank")}
                          className="rounded-lg"
                          title="Open in new tab"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        {canManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`join-link-revoke-${l.token}`}
                            onClick={() => revoke(l.token)}
                            className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>

            {/* Revoked */}
            {revokedLinks.length > 0 && (
              <section className="mt-8 space-y-2">
                <h2 className="text-sm font-semibold text-slate-500">Revoked ({revokedLinks.length})</h2>
                {revokedLinks.map((l) => (
                  <div key={l.token} className="bg-slate-50 rounded-xl border border-slate-200 p-3 flex items-center justify-between">
                    <div className="text-xs text-slate-500 truncate">
                      {l.label || "Untitled"} · {l.submission_count || 0} signups · revoked {l.revoked_at ? fmt(l.revoked_at, "dd MMM yyyy", default_timezone) : ""}
                    </div>
                    {canManage && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(l.token)}
                        className="rounded-lg text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </section>
            )}
          </motion.div>
        </main>
      </div>
      <BottomNav user={user} onLogout={onLogout} />
      {confirmDialog}
    </div>
  );
};

export default JoinLinksManagement;
